import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DollarSign, Globe2, Shield, Star, Clock, MapPin, 
  ChevronRight, Check, ArrowRight, Menu, X, User,
  Zap, Droplet, Flame, Wrench, Hammer, Building2,
  BadgeCheck, TrendingUp, Wallet, Calendar, Users,
  ChevronDown, LogOut, Phone, Mail, CreditCard,
  CheckCircle2, XCircle, Lock, Briefcase, Award
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ========== SEO META DATA ==========
// Meta Title: Become a ZOLT Pro | Earn More as a Contractor | Global Opportunities
// Meta Description: Join ZOLT's global contractor network. Earn £1,200-£4,500/month with fair pricing, weekly payouts, and full control. No subscriptions. Start free today.

// ========== TRADE DATA ==========
const TRADES = [
  { id: 'plumbing', name: 'Plumbing', icon: Droplet, color: 'text-blue-500', earnings: { min: 1200, max: 3200 } },
  { id: 'electrical', name: 'Electrical', icon: Zap, color: 'text-yellow-500', earnings: { min: 1400, max: 3800 } },
  { id: 'heating', name: 'Heating & Gas', icon: Flame, color: 'text-orange-500', earnings: { min: 1500, max: 4200 } },
  { id: 'handyman', name: 'Handyman', icon: Wrench, color: 'text-zinc-600', earnings: { min: 900, max: 2400 } },
  { id: 'carpentry', name: 'Carpentry', icon: Hammer, color: 'text-amber-700', earnings: { min: 1100, max: 2800 } },
  { id: 'cleaning', name: 'Cleaning', icon: Building2, color: 'text-green-500', earnings: { min: 800, max: 2200 } },
  { id: 'appliances', name: 'Appliance Repair', icon: Wrench, color: 'text-purple-500', earnings: { min: 1000, max: 2600 } },
  { id: 'other', name: 'Other Trade', icon: Briefcase, color: 'text-zinc-500', earnings: { min: 900, max: 2500 } },
];

// Country data with currency
const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', currency: '£', multiplier: 1.0 },
  { code: 'US', name: 'United States', currency: '$', multiplier: 1.25 },
  { code: 'DE', name: 'Germany', currency: '€', multiplier: 1.1 },
  { code: 'FR', name: 'France', currency: '€', multiplier: 1.05 },
  { code: 'AU', name: 'Australia', currency: 'A$', multiplier: 1.5 },
  { code: 'CA', name: 'Canada', currency: 'C$', multiplier: 1.3 },
  { code: 'AE', name: 'UAE', currency: 'AED', multiplier: 4.5 },
  { code: 'SG', name: 'Singapore', currency: 'S$', multiplier: 1.7 },
  { code: 'NL', name: 'Netherlands', currency: '€', multiplier: 1.08 },
  { code: 'IE', name: 'Ireland', currency: '€', multiplier: 1.02 },
];

// Recent job examples
const RECENT_JOBS = [
  { type: 'Emergency plumbing repair', value: '£220', duration: 'Same day', location: 'London, UK', trade: 'plumbing' },
  { type: 'Electrical fault diagnosis', value: '€185', duration: '2 hours', location: 'Berlin, DE', trade: 'electrical' },
  { type: 'Boiler service & repair', value: '£340', duration: '3 hours', location: 'Manchester, UK', trade: 'heating' },
  { type: 'Kitchen tap replacement', value: '$145', duration: '1 hour', location: 'New York, US', trade: 'plumbing' },
  { type: 'Smart thermostat install', value: '€210', duration: '2 hours', location: 'Paris, FR', trade: 'electrical' },
  { type: 'Property inspection', value: 'A$280', duration: '90 mins', location: 'Sydney, AU', trade: 'handyman' },
];

const BecomeContractorPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Earnings calculator state
  const [selectedTrade, setSelectedTrade] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [availability, setAvailability] = useState('full-time');
  const [estimatedEarnings, setEstimatedEarnings] = useState(null);
  const [showEstimate, setShowEstimate] = useState(false);

  // Calculate earnings
  useEffect(() => {
    if (selectedTrade) {
      const trade = TRADES.find(t => t.id === selectedTrade);
      const country = COUNTRIES.find(c => c.code === selectedCountry);
      
      if (trade && country) {
        const availabilityMultiplier = availability === 'full-time' ? 1 : 0.5;
        const min = Math.round(trade.earnings.min * country.multiplier * availabilityMultiplier);
        const max = Math.round(trade.earnings.max * country.multiplier * availabilityMultiplier);
        
        setEstimatedEarnings({
          min,
          max,
          currency: country.currency
        });
        setShowEstimate(true);
      }
    }
  }, [selectedTrade, selectedCountry, availability]);

  const handleStartOnboarding = () => {
    if (user) {
      // Store return URL and go to onboarding
      sessionStorage.setItem('contractorOnboardingReturnTo', '/become-contractor');
      navigate('/contractor/onboarding');
    } else {
      navigate('/auth?mode=contractor');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ========== NAVIGATION ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="text-2xl font-bold tracking-tight">ZOLT</Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/#how-it-works" className="text-neutral-600 hover:text-black text-sm font-medium">How it works</Link>
                <Link to="/how-pricing-works" className="text-neutral-600 hover:text-black text-sm font-medium">Pricing</Link>
                <span className="text-black font-semibold text-sm">Become a Pro</span>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-zinc-600" />
                      </div>
                      <span className="text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <Briefcase className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/auth?mode=login')}>
                    Log in
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth?mode=contractor')}
                    className="bg-black hover:bg-zinc-800 text-white rounded-full px-6"
                  >
                    Get started
                  </Button>
                </>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-neutral-100"
            >
              <div className="px-4 py-4 space-y-3">
                <Link to="/#how-it-works" className="block py-2 text-neutral-600">How it works</Link>
                <Link to="/how-pricing-works" className="block py-2 text-neutral-600">Pricing</Link>
                <hr className="border-neutral-200" />
                {user ? (
                  <>
                    <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
                      Dashboard
                    </Button>
                    <Button variant="ghost" onClick={handleLogout} className="w-full text-red-600">
                      Log out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => navigate('/auth?mode=login')} className="w-full">
                      Log in
                    </Button>
                    <Button onClick={() => navigate('/auth?mode=contractor')} className="w-full bg-black text-white">
                      Get started
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ========== HERO + EARNINGS ESTIMATOR ========== */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-zinc-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left: Hero Copy */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 mb-6">
                Earn more.
                <br />
                <span className="text-zinc-400">Work smarter.</span>
              </h1>
              <p className="text-lg sm:text-xl text-zinc-600 mb-8 max-w-xl mx-auto lg:mx-0">
                Join thousands of verified professionals earning on their own terms. 
                Fair pricing, weekly payouts, and jobs that match your skills — locally or globally.
              </p>
              
              {/* Trust signals */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>No subscriptions</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Weekly payouts</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Full control</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 p-6 bg-white rounded-2xl border border-zinc-100 shadow-sm max-w-md mx-auto lg:mx-0">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-zinc-900">50k+</div>
                  <div className="text-xs sm:text-sm text-zinc-500">Jobs completed</div>
                </div>
                <div className="text-center border-x border-zinc-100">
                  <div className="text-2xl sm:text-3xl font-bold text-zinc-900">4.8</div>
                  <div className="text-xs sm:text-sm text-zinc-500">Avg. rating</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-zinc-900">30+</div>
                  <div className="text-xs sm:text-sm text-zinc-500">Countries</div>
                </div>
              </div>
            </motion.div>

            {/* Right: Earnings Calculator */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="order-first lg:order-last"
            >
              <div className="bg-white rounded-3xl shadow-2xl shadow-zinc-200/50 border border-zinc-100 p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full mb-4">
                    <TrendingUp className="w-4 h-4" />
                    Earnings Estimator
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900">
                    See how much you could earn
                  </h2>
                </div>

                <div className="space-y-4">
                  {/* Trade Selection */}
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-2 block">Your trade / service</label>
                    <Select value={selectedTrade} onValueChange={setSelectedTrade}>
                      <SelectTrigger className="h-12 bg-zinc-50 border-zinc-200" data-testid="trade-select">
                        <SelectValue placeholder="Select your trade" />
                      </SelectTrigger>
                      <SelectContent>
                        {TRADES.map(trade => {
                          const Icon = trade.icon;
                          return (
                            <SelectItem key={trade.id} value={trade.id}>
                              <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 ${trade.color}`} />
                                {trade.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Country Selection */}
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-2 block">Your location</label>
                    <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                      <SelectTrigger className="h-12 bg-zinc-50 border-zinc-200" data-testid="country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country.code} value={country.code}>
                            <div className="flex items-center gap-2">
                              <Globe2 className="w-4 h-4 text-zinc-400" />
                              {country.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-2 block">Availability</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setAvailability('full-time')}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          availability === 'full-time'
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 text-zinc-700 hover:border-zinc-300'
                        }`}
                        data-testid="availability-fulltime"
                      >
                        Full-time
                      </button>
                      <button
                        onClick={() => setAvailability('part-time')}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          availability === 'part-time'
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 text-zinc-700 hover:border-zinc-300'
                        }`}
                        data-testid="availability-parttime"
                      >
                        Part-time
                      </button>
                    </div>
                  </div>

                  {/* Earnings Display */}
                  <AnimatePresence>
                    {showEstimate && estimatedEarnings && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 text-center border border-green-100"
                      >
                        <p className="text-sm text-green-700 mb-2">Estimated monthly earnings</p>
                        <p className="text-4xl sm:text-5xl font-bold text-zinc-900 mb-2">
                          {estimatedEarnings.currency}{estimatedEarnings.min.toLocaleString()} – {estimatedEarnings.currency}{estimatedEarnings.max.toLocaleString()}
                        </p>
                        <p className="text-xs text-zinc-500">
                          Based on current job demand and average completed jobs
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* CTA Button */}
                  <Button
                    onClick={handleStartOnboarding}
                    className="w-full h-14 bg-zinc-900 hover:bg-zinc-800 text-white text-lg rounded-xl"
                    data-testid="start-earning-btn"
                  >
                    {showEstimate ? 'Start earning with ZOLT' : 'See my earning potential'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-xs text-center text-zinc-400">
                    Estimates vary based on availability, demand, and job acceptance. Past performance does not guarantee future earnings.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== GLOBAL ADVANTAGE SECTION ========== */}
      <section className="py-16 md:py-24 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-6">
              <Globe2 className="w-4 h-4" />
              Global Network
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Earn locally or globally
              <br />
              <span className="text-zinc-400">Your skills, your rules</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              ZOLT connects you with opportunities across 30+ countries. 
              One account, unlimited potential — work where you are or wherever your skills take you.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              { icon: MapPin, title: 'Work in multiple cities', desc: 'Accept jobs in any supported location' },
              { icon: Globe2, title: 'Cross-border opportunities', desc: 'Travel and earn internationally' },
              { icon: Users, title: 'Partner-fulfilled jobs', desc: 'Collaborate with local professionals' },
              { icon: Wallet, title: 'Multi-currency payouts', desc: 'Get paid in your preferred currency' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== WHY CONTRACTORS CHOOSE ZOLT ========== */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 mb-4">
              Why contractors choose ZOLT
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Built by professionals, for professionals. Everything you need to grow your business.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: DollarSign,
                title: 'Earn more, faster',
                points: ['Fair, transparent job pricing', 'Weekly payouts to your account', 'No chasing customers for payment'],
                color: 'bg-green-50 text-green-600'
              },
              {
                icon: XCircle,
                title: 'No subscriptions',
                points: ['No monthly fees ever', 'Pay only when you earn', 'Cancel anytime — no penalties'],
                color: 'bg-red-50 text-red-600'
              },
              {
                icon: MapPin,
                title: 'Full control',
                points: ['Choose your service radius', 'Accept or decline any job', 'Set your own availability'],
                color: 'bg-blue-50 text-blue-600'
              },
              {
                icon: Star,
                title: 'Verified reputation',
                points: ['Real customer reviews', 'Trust badges for verification', 'Higher visibility = more jobs'],
                color: 'bg-yellow-50 text-yellow-600'
              },
              {
                icon: Shield,
                title: 'Built for professionals',
                points: ['Quality jobs, serious clients', 'Not a race-to-the-bottom platform', 'Professional support team'],
                color: 'bg-purple-50 text-purple-600'
              },
              {
                icon: Clock,
                title: 'Work on your schedule',
                points: ['No minimum hours required', 'Go online when you want', 'Part-time or full-time flexibility'],
                color: 'bg-zinc-100 text-zinc-600'
              },
            ].map((benefit, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-6 border border-zinc-100 hover:border-zinc-200 hover:shadow-lg transition-all"
              >
                <div className={`w-12 h-12 ${benefit.color} rounded-xl flex items-center justify-center mb-4`}>
                  <benefit.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-3">{benefit.title}</h3>
                <ul className="space-y-2">
                  {benefit.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-600">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== RECENT JOBS SECTION ========== */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Recent jobs on ZOLT
            </h2>
            <p className="text-lg text-zinc-600">
              Real opportunities from contractors like you
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {RECENT_JOBS.map((job, idx) => {
              const trade = TRADES.find(t => t.id === job.trade);
              const Icon = trade?.icon || Wrench;
              return (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  className="bg-white rounded-xl p-5 border border-zinc-200 hover:border-zinc-300 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${trade?.color || 'text-zinc-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-zinc-900 truncate">{job.type}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-zinc-500">
                        <MapPin className="w-3 h-3" />
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{job.value}</p>
                      <p className="text-xs text-zinc-400">{job.duration}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <p className="text-center text-sm text-zinc-400 mt-6">
            Job examples shown are indicative and vary by location and availability
          </p>
        </div>
      </section>

      {/* ========== VERIFICATION SECTION ========== */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full mb-6">
                <BadgeCheck className="w-4 h-4" />
                Verification
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-6">
                Unlock better jobs with verification
              </h2>
              <p className="text-lg text-zinc-600 mb-8">
                Verified contractors get priority access to higher-paying jobs and corporate clients. 
                Stand out from the crowd with trust badges on your profile.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  'Access higher-paying and corporate jobs',
                  'Priority job dispatch in your area',
                  'Visible trust badge on your profile',
                  'Increased customer confidence and bookings',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-zinc-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                <Clock className="w-5 h-5 text-zinc-400" />
                <p className="text-sm text-zinc-600">
                  <span className="font-medium text-zinc-900">Quick onboarding</span> — 
                  Most contractors complete setup in under 5 minutes
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="relative"
            >
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-3xl p-8 text-white">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">ZOLT Pro</h3>
                    <p className="text-zinc-400">Verified contractor</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">ID Verified</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-sm">Insurance Confirmed</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm">Certified Professional</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm text-zinc-300">5.0 (127 reviews)</span>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-green-100 rounded-full blur-3xl opacity-50" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-100 rounded-full blur-3xl opacity-50" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== OBJECTION HANDLING SECTION ========== */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              You&apos;re always in control
            </h2>
            <p className="text-lg text-zinc-600">
              No surprises, no lock-ins, no pressure
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 gap-4"
          >
            {[
              { icon: Check, text: 'Accept or decline any job — your choice', color: 'text-green-500' },
              { icon: DollarSign, text: 'See the full price before accepting', color: 'text-green-500' },
              { icon: Briefcase, text: 'No exclusivity — work elsewhere too', color: 'text-green-500' },
              { icon: Users, text: 'Support team available if issues arise', color: 'text-green-500' },
              { icon: Calendar, text: 'Set your own schedule and availability', color: 'text-green-500' },
              { icon: Lock, text: 'Your data is secure and never sold', color: 'text-green-500' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="flex items-center gap-3 bg-white rounded-xl p-4 border border-zinc-200"
              >
                <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <span className="text-zinc-700 font-medium">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== FINAL CTA SECTION ========== */}
      <section className="py-16 md:py-24 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to grow your business?
            </h2>
            <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
              Join thousands of professionals already earning more with ZOLT. 
              Set up your profile in minutes and start receiving job offers today.
            </p>

            <Button
              onClick={handleStartOnboarding}
              size="lg"
              className="h-14 px-8 bg-white hover:bg-zinc-100 text-zinc-900 text-lg rounded-full"
              data-testid="final-cta-btn"
            >
              See my earning potential
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="text-sm text-zinc-500 mt-6">
              Free to join • No subscriptions • No obligation
            </p>
          </motion.div>
        </div>
      </section>

      {/* ========== FAQ SECTION (SEO + Trust) ========== */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Common questions
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {[
              {
                q: 'How much does it cost to join ZOLT?',
                a: 'Nothing upfront. ZOLT is free to join with no monthly subscriptions. We only take a small platform fee when you complete a job and get paid.'
              },
              {
                q: 'How do I get paid?',
                a: 'We process payouts weekly to your connected bank account. You can track all your earnings in real-time through your dashboard.'
              },
              {
                q: 'Can I work in multiple locations?',
                a: 'Yes! Your ZOLT account works globally. Set your service radius, travel to new cities, or even accept remote/digital jobs where applicable.'
              },
              {
                q: 'Do I need to be verified?',
                a: 'You can start accepting jobs right away. However, verified contractors get priority access to higher-paying jobs and trust badges that boost customer confidence.'
              },
              {
                q: 'Can I decline jobs?',
                a: 'Absolutely. You have full control over which jobs you accept. There are no penalties for declining jobs that don&apos;t suit your schedule or expertise.'
              },
              {
                q: 'Is there a minimum number of hours I need to work?',
                a: 'No minimums. Work as much or as little as you want. ZOLT is designed for both full-time professionals and those seeking part-time opportunities.'
              },
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="bg-zinc-50 rounded-xl p-6 border border-zinc-100"
                itemScope
                itemType="https://schema.org/Question"
              >
                <h3 className="font-semibold text-zinc-900 mb-2" itemProp="name">{faq.q}</h3>
                <p className="text-zinc-600" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                  <span itemProp="text">{faq.a}</span>
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-12 bg-zinc-50 border-t border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-2xl font-bold tracking-tight">ZOLT</div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-600">
              <Link to="/" className="hover:text-black">Home</Link>
              <Link to="/#how-it-works" className="hover:text-black">How it works</Link>
              <Link to="/how-pricing-works" className="hover:text-black">Pricing</Link>
              <a href="#" className="hover:text-black">Privacy</a>
              <a href="#" className="hover:text-black">Terms</a>
              <a href="#" className="hover:text-black">Contact</a>
            </div>
            <p className="text-sm text-zinc-500">© 2026 ZOLT X Limited</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BecomeContractorPage;
